import React, {ReactNode} from "react";
import {styled, Text} from "@reside/ui";

import {WorkflowType} from "../../utils/enums";
import {
  getWorkflowLabel,
  resolveWorkflowType,
} from "../../atoms/start-workflow/workflowMapping";

export type WorkflowStyle = {
  /** Pastel hex for text, ribbon border, and list-row accent. */
  color: string;
  /** Soft tint of `color` for badge backgrounds (rgba). */
  bg: string;
};

const BG_ALPHA = 0.2;

/**
 * Curated workflow palette (three 8-color sets provided by design), used as a
 * stable hash fallback for workflow names without a fixed entry in
 * WORKFLOW_STYLE_OVERRIDES below. #682D6C and #07A4D5 are intentionally
 * excluded here — they're the confirmed override colors for arbitration and
 * admission, so keeping them in this pool would let an unrelated/unknown
 * workflow hash to a color visually indistinguishable from a confirmed one.
 */
const WORKFLOW_PALETTE: ReadonlyArray<string> = [
  "#568D4E",
  "#5D46A0",
  "#6E19A3",
  "#DDE7AB",
  "#A6F7AF",
  "#59D7DE",
  "#D279A1",
  "#022631",
  "#87D4BF",
  "#4FD2FA",
  "#C9512C",
  "#55D327",
  "#BAEDFD",
  "#7F3D70",
  "#D4CE5E",
  "#8C88B9",
  "#2D364D",
  "#DC5BB9",
  "#D8A6A6",
  "#CC2E5F",
  "#483F5A",
  "#B3A9BC",
];

/** Supported workflows mapped to palette colors. Keys are lowercase template names. */
const WORKFLOW_STYLE_OVERRIDES: Record<string, string> = {
  [WorkflowType.ADMISSION]: "#07A4D5",
  [WorkflowType.ARBITRATION]: "#682D6C",
  [WorkflowType.ADMISSION_SPANISH]: "#008080", // Teal
  [WorkflowType.READMISSION]: "#FFC107", // Gold/Amber
  [WorkflowType.READMISSION_SPANISH]: "#CC5500", // Burnt Orange
};

/** Unknown template workflows — stable hash pick from the full palette. */
const FALLBACK_PALETTE = WORKFLOW_PALETTE;

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "");
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const toStyle = (color: string): WorkflowStyle => ({
  color,
  bg: hexToRgba(color, BG_ALPHA),
});

/** Stable non-cryptographic hash → palette index. */
const hashToPaletteIndex = (value: string, paletteSize: number): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash % paletteSize;
};

export const getWorkflowStyle = (workflowType?: string): WorkflowStyle => {
  const key = resolveWorkflowType(workflowType);

  const override = WORKFLOW_STYLE_OVERRIDES[key];
  if (override) {
    return toStyle(override);
  }

  const color =
    FALLBACK_PALETTE[hashToPaletteIndex(key, FALLBACK_PALETTE.length)];
  return toStyle(color);
};

type Props = {
  workflowType?: string;
  /** Star icon button — rendered on the left. */
  star: ReactNode;
  /** Options / split-button menu — rendered on the right. */
  actions: ReactNode;
};

/**
 * Full-bleed colored ribbon at the top of a dashboard (tile) card.
 *
 * Uses negative margins (-15px on 3 sides) to escape `DashboardCell`'s
 * `padding: 15px` and fill edge-to-edge. Top border uses the solid workflow
 * color; label text uses the same color for hierarchy.
 *
 * List view uses a left accent border via `--row-accent-color` from
 * `getWorkflowStyle(...).color` instead of this ribbon.
 */
export const WorkflowRibbon = ({workflowType, star, actions}: Props) => {
  const {color} = getWorkflowStyle(workflowType);
  const label = getWorkflowLabel(workflowType);

  return (
    <RibbonWrapper color={color}>
      {star}
      {label && (
        <LabelSlot>
          <Text
            fontSize={2}
            fontWeight="bold"
            lineHeight="16px"
            style={{color}}
          >
            {label}
          </Text>
        </LabelSlot>
      )}
      <ActionsSlot>{actions}</ActionsSlot>
    </RibbonWrapper>
  );
};

const RibbonWrapper = styled.div<{color: string}>`
  /* Bleed to card edges by reversing the card's padding: 15px */
  margin: -15px -15px 16px -15px;
  border-radius: 3px 3px 0 0;
  padding: 4px 10px 4px 12px;
  background: transparent;
  border-top: 4px solid ${({color}) => color};

  display: flex;
  align-items: center;
  min-height: 36px;
`;

const LabelSlot = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ActionsSlot = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
